/*
  Styled Google Map for sections/custom-contact-and-map.liquid.
  Loads the Maps JavaScript API once, then draws a map in the section's
  colors. If the API key is rejected, falls back to the plain embed.
*/
(() => {
  if (customElements.get('contact-map')) return;

  let mapsApiPromise;

  function loadMapsApi(apiKey) {
    if (window.google && window.google.maps && window.google.maps.Map) return Promise.resolve();
    if (mapsApiPromise) return mapsApiPromise;

    mapsApiPromise = new Promise((resolve, reject) => {
      window.__contactMapReady = resolve;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=__contactMapReady&loading=async`;
      script.async = true;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return mapsApiPromise;
  }

  // Google calls this global when the key is invalid or not authorized for the domain.
  window.gm_authFailure = () => {
    document.querySelectorAll('contact-map[data-api-key]').forEach((el) => el.showFallback());
  };

  function mix(hexA, hexB, weight) {
    const a = hexA.replace('#', '');
    const b = hexB.replace('#', '');
    const channel = (i) =>
      Math.round(parseInt(a.substr(i, 2), 16) * (1 - weight) + parseInt(b.substr(i, 2), 16) * weight)
        .toString(16)
        .padStart(2, '0');
    return `#${channel(0)}${channel(2)}${channel(4)}`;
  }

  function measureText(text, font) {
    const context = document.createElement('canvas').getContext('2d');
    context.font = font;
    return Math.ceil(context.measureText(text).width);
  }

  function buildStyles({ land, water, road, label, marker }) {
    const park = mix(land, water, 0.12);
    const highway = mix(road, marker, 0.25);
    const boundary = mix(land, label, 0.35);

    return [
      { elementType: 'geometry', stylers: [{ color: land }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: label }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: land }, { weight: 3 }] },
      { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: boundary }] },
      { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ visibility: 'on' }, { color: park }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: road }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: highway }] },
      { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: mix(label, land, 0.25) }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: water }] },
      { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: land }] },
      { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: water }] },
    ];
  }

  class ContactMap extends HTMLElement {
    connectedCallback() {
      const { apiKey } = this.dataset;
      if (!apiKey) return;

      loadMapsApi(apiKey)
        .then(() => this.render())
        .catch(() => this.showFallback());
    }

    render() {
      const canvas = this.querySelector('.contact-map__canvas');
      const lat = parseFloat(this.dataset.lat);
      const lng = parseFloat(this.dataset.lng);
      if (!canvas || Number.isNaN(lat) || Number.isNaN(lng)) {
        this.showFallback();
        return;
      }

      const colors = {
        land: this.dataset.colorLand,
        water: this.dataset.colorWater,
        road: this.dataset.colorRoad,
        label: this.dataset.colorLabel,
        marker: this.dataset.colorMarker,
      };
      const position = { lat, lng };
      const natural = this.dataset.mapStyle !== 'brand';
      const placeName = this.dataset.placeName;

      const map = new google.maps.Map(canvas, {
        center: position,
        zoom: parseInt(this.dataset.zoom, 10) || 14,
        // Natural: Google's own colors and places (softened by CSS when tinted)
        // (area names like the town are hidden so they don't collide with the business name)
        styles: natural
          ? [{ featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'off' }] }]
          : buildStyles(colors),
        disableDefaultUI: true,
        cameraControl: true,
        gestureHandling: 'cooperative',
      });

      // Classic map pin with a dark center, plus the business name beside it
      const pinColor = natural ? '#EA4335' : colors.marker;
      const pinStroke = natural ? '#B31412' : mix(colors.marker, '#000000', 0.25);
      const pinSvg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="27" height="43" viewBox="0 0 27 43">` +
        `<path d="M13.5 1C6.6 1 1 6.6 1 13.5c0 9.4 12.5 28 12.5 28S26 22.9 26 13.5C26 6.6 20.4 1 13.5 1z" fill="${pinColor}" stroke="${pinStroke}" stroke-width="1.5"/>` +
        `<circle cx="13.5" cy="13.5" r="4.6" fill="${pinStroke}"/></svg>`;

      // Name beside the pin, wrapped to a width that fits its longest word pair
      const labelFont = '500 14px Roboto, Arial, sans-serif';
      const labelWidth = placeName ? Math.min(measureText(placeName, labelFont), 110) : 0;

      new google.maps.Marker({
        position,
        map,
        title: placeName || this.dataset.title,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(pinSvg)}`,
          scaledSize: new google.maps.Size(27, 43),
          anchor: new google.maps.Point(13.5, 42),
          // Label centered this far right, so its left edge starts just past the pin
          labelOrigin: new google.maps.Point(27 + 4 + labelWidth / 2, 12),
        },
        label: placeName
          ? {
              text: placeName,
              color: natural ? '#C5221F' : colors.label,
              fontFamily: 'Roboto, Arial, sans-serif',
              fontSize: '14px',
              fontWeight: '500',
              className: 'contact-map__marker-label',
            }
          : null,
      });

      // Show the place card (hidden until the styled map has loaded)
      const panel = this.querySelector('.contact-map__panel');
      if (panel) panel.hidden = false;
    }

    showFallback() {
      const template = this.querySelector('.contact-map__fallback');
      if (!template) return;
      this.replaceChildren(template.content.cloneNode(true));
    }
  }

  customElements.define('contact-map', ContactMap);
})();
